import { create } from 'zustand';

/**
 * Los comensales a los que pertenece una línea.
 *
 * Es una LISTA y no un número porque los tres casos son reales y solo el
 * conjunto los expresa sin casos especiales:
 *
 *   [1]      el plato de uno
 *   [1, 3]   la botella que pidieron dos      ← lo que un número no puede decir
 *   []       de la mesa: se reparte entre todos
 *
 * Se guarda ordenada y sin repetidos para que la identidad de la línea sea
 * estable: [3,1] y [1,3] son la misma botella.
 */
export const DE_LA_MESA = [];

/**
 * Identidad estable de una línea, la que NO cambia nunca.
 *
 * `lineId` no sirve para esto: codifica el tiempo y los comensales, así que
 * pasar un platillo del comensal 1 al 2 le cambia la identidad. Para el carrito
 * eso está bien —es lo que hace que dos guacamoles de comensales distintos sean
 * dos renglones—, pero para fundir la cuenta entre dos tablets es un desastre:
 * mover un platillo se lee como "borré uno y creé otro", y si las dos tablets
 * mueven el mismo, la mesa acaba con dos guacamoles y el cliente paga uno que
 * no existe.
 *
 * Las cuentas guardadas antes de esto no lo traen: ahí se usa el `lineId` como
 * identidad. Tiene que ser determinista y no aleatorio, porque las dos tablets
 * han de deducir el MISMO identificador para la misma línea vieja.
 */
export const nuevoUid = () => (globalThis.crypto?.randomUUID
  ? globalThis.crypto.randomUUID()
  : `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`);

export const uidDe = (item) => item.uid ?? item.lineId;

export const normSeats = (seats) =>
  [...new Set((Array.isArray(seats) ? seats : [seats]).filter((n) => Number.isInteger(n) && n > 0))]
    .sort((a, b) => a - b);

/**
 * Los comensales sentados en la mesa, saneados.
 *
 * `n` es el número que ve el mesero y la llave que usan las líneas en `seats`.
 * `nombre` es el apodo corto opcional —"Sra", "Niño"— para reconocer en vez de
 * recordar: en una mesa de ocho, "C6" no le dice nada a nadie.
 */
export const normComensales = (lista) => {
  if (!Array.isArray(lista)) return [];
  const vistos = new Map();
  for (const c of lista) {
    const n = Number(typeof c === 'object' && c !== null ? c.n : c);
    if (!Number.isInteger(n) || n < 1 || n > 99 || vistos.has(n)) continue;
    vistos.set(n, { n, nombre: String(c?.nombre ?? '').trim().slice(0, 12) });
  }
  return [...vistos.values()].sort((a, b) => a.n - b.n);
};

// Cómo se llama un comensal en pantalla: su apodo si lo tiene, si no "C3".
export const nombreDe = (n, comensales = []) =>
  comensales.find((c) => c.n === n)?.nombre?.trim() || `C${n}`;

// Etiqueta corta para el chip de la línea.
export const seatsLabel = (seats, comensales = []) => {
  const l = normSeats(seats);
  if (!l.length)      return 'Mesa';
  if (l.length === 1) return nombreDe(l[0], comensales);
  if (l.length <= 3)  return l.map((n) => nombreDe(n, comensales)).join('·');
  return `${l.length} comensales`;
};

// Identidad de una línea del carrito.
// Sin modificadores es el id del producto tal cual, para que los controles de
// cantidad del catálogo (que solo conocen el producto) sigan funcionando.
// Con modificadores se le agrega una firma: el mismo platillo pedido "sin
// cebolla" y "con tocino" son dos líneas distintas, no una con cantidad 2.
/** La nota de cocina como entra en la identidad: sin espacios de más ni
 *  mayúsculas, para que "Sin cebolla" y "sin cebolla " sean la misma línea. */
export const normNota = (nota) => String(nota ?? '').trim().replace(/\s+/g, ' ').slice(0, 60);

export const lineIdFor = (product, modifiers = [], course = 1, seats = [], note = '') => {
  // El tiempo y los comensales solo entran en la identidad cuando hay algo que
  // distinguir. Así los controles de cantidad del catálogo —que solo conocen el
  // producto— siguen funcionando cuando nadie usa mesas ni comensales, y a la
  // vez dos guacamoles del comensal 1 y uno del 3 son líneas distintas y no una
  // con cantidad 3.
  const sufijoTiempo   = course > 1 ? `::t${course}` : '';
  const lista = normSeats(seats);
  const sufijoComensal = lista.length ? `::c${lista.join('-')}` : '';
  // La nota también distingue: un taco "sin cebolla" y uno normal son dos
  // renglones aunque sean el mismo producto, el mismo tiempo y el mismo
  // comensal. Si no, agregar otro taco desde el catálogo se sumaba al que
  // traía la nota y la cocina recibía dos sin cebolla.
  const nota = normNota(note).toLowerCase();
  const sufijoNota = nota ? `::n${nota}` : '';
  const cola = `${sufijoTiempo}${sufijoComensal}${sufijoNota}`;
  if (!modifiers.length) return `${product._id}${cola}`;
  const firma = modifiers
    .map((m) => `${m.group_name}:${m.option_name}`)
    .sort()
    .join('|');
  return `${product._id}::${firma}${cola}`;
};

// Nombres de los tiempos del servicio. Son etiquetas, no configuración: un
// restaurante que sirva de otra forma sigue pudiendo usar los números.
export const COURSE_NAMES = { 1: 'Entrada', 2: 'Fuerte', 3: 'Postre' };
export const courseName = (n) => COURSE_NAMES[n] ?? `${n}º tiempo`;

/**
 * Número o cero. Todo el dinero del carrito pasa por aquí.
 *
 * No es paranoia decorativa: un solo `undefined` en un precio contamina el
 * subtotal, el botón de cobrar y la pantalla de checkout con `NaN`, y el
 * servidor rechaza la venta con «total debe ser mayor a 0». La mesa se vuelve
 * incobrable y el cajero no tiene forma de saber por qué.
 */
export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const sumDeltas = (modifiers = []) =>
  modifiers.reduce((acc, m) => acc + num(m.price_delta), 0);

/**
 * Reconstruye el precio unitario de una línea que no lo trae.
 *
 * Hay cuentas viejas guardadas sin `unit_price`. El precio se recupera del
 * total que sí quedó grabado —que es el que el cliente ya vio y aceptó— y solo
 * si tampoco está se recurre al precio actual del producto, que pudo cambiar
 * desde que se abrió la mesa.
 */
export const unitPriceDe = (item) => {
  if (Number.isFinite(Number(item.unit_price))) return Number(item.unit_price);

  const qty = num(item.quantity);
  if (qty > 0 && Number.isFinite(Number(item.total))) return Number(item.total) / qty;

  return num(item.product?.price) + sumDeltas(item.modifiers ?? []);
};

/**
 * Funde en uno los renglones que comparten `lineId`.
 *
 * No debería haberlos —`addItem` suma sobre la línea existente— pero hay
 * cuentas guardadas con duplicados, y con ellos todo se descompone: los
 * controles de cantidad hacen `map` sobre el `lineId` y mueven los dos a la
 * vez, y `mergeTabItems` arma un Map por `lineId`, así que al guardar tira uno
 * sin avisar. Se fusionan al cargar, que es la única vez que se ven todos.
 */
const fundirDuplicados = (items) => {
  const porId = new Map();

  for (const item of items) {
    const previo = porId.get(item.lineId);
    if (!previo) { porId.set(item.lineId, item); continue; }

    const quantity = previo.quantity + item.quantity;
    porId.set(item.lineId, {
      ...previo,
      quantity,
      // Lo ya enviado a cocina se suma, pero nunca puede exceder lo pedido.
      fired_qty: Math.min(quantity, (previo.fired_qty ?? 0) + (item.fired_qty ?? 0)),
      total:     quantity * previo.unit_price,
    });
  }

  return [...porId.values()];
};

/**
 * Lo que falta mandar a preparar: la diferencia entre lo pedido y lo ya
 * enviado. Si el mesero agrega platillos a una mesa, solo salen los nuevos.
 * Bajar la cantidad no genera pendientes negativos.
 *
 * Es función pura y no un getter del store a propósito: devuelve un array
 * nuevo cada vez, y como selector de Zustand provocaría un render infinito.
 * Se consume con useMemo sobre `items`.
 */
export const pendingKitchenItems = (items = []) =>
  items
    .map((i) => ({ item: i, pending: Math.max(0, i.quantity - (i.fired_qty ?? 0)) }))
    .filter((x) => x.pending > 0);

// Carrito — estado en memoria, no se persiste (se pierde al cerrar la app,
// que es el comportamiento correcto para un carrito de POS)
export const useCartStore = create((set, get) => ({
  items: [],       // [{ lineId, product, quantity, base_price, unit_price, modifiers, seat, total }]

  // A quién se le apunta lo que se agregue ahora. Vive con el carrito y no en
  // otro lado porque es parte de la captura: al vaciar, vuelve a la mesa.
  activeSeat: null,   // número de comensal, o null para "de la mesa"

  // Quiénes están sentados. Es estado propio y NO se deduce de los platillos.
  //
  // Antes la lista se sacaba de `items`, así que un comensal al que todavía no
  // se le pedía nada no existía: el mesero lo daba de alta, tocaba otra cosa y
  // desaparecía sin aviso, y al recargar se perdían todos los que no habían
  // ordenado. Una mesa de seis donde tres no han pedido es una mesa de seis.
  comensales: [],     // [{ n, nombre }]
  customer: null,  // Customer | null
  discount: 0,     // importe fijo de descuento
  note: '',        // nota libre de la venta

  // ─── Items ───────────────────────────────────────────────────────────────────

  addItem: (product, modifiers = [], quantity = 1, course = 1, seats = DE_LA_MESA) => {
    const items  = get().items;
    const lista  = normSeats(seats);
    const lineId = lineIdFor(product, modifiers, course, lista);
    const idx    = items.findIndex((i) => i.lineId === lineId);

    if (idx >= 0) {
      const updated = [...items];
      const qty     = updated[idx].quantity + quantity;
      updated[idx]  = { ...updated[idx], quantity: qty, total: qty * num(updated[idx].unit_price) };
      set({ items: updated });
      return;
    }

    const basePrice = num(product.price);
    const unitPrice = basePrice + sumDeltas(modifiers);
    set({
      items: [...items, {
        lineId,
        uid: nuevoUid(),     // identidad estable; `lineId` cambia al mover de comensal
        product,
        modifiers,
        quantity,
        course,                 // tiempo del servicio: 1 entrada, 2 fuerte, 3 postre
        seats: lista,           // comensales; vacío = de la mesa
        note:  '',              // nota para cocina de ESTA línea ("sin cebolla")
        fired_qty:  0,          // cuánto de esta línea ya se mandó a preparar
        base_price: basePrice,
        unit_price: unitPrice,
        total:      quantity * unitPrice,
      }],
    });
  },

  removeItem: (lineId) =>
    set({ items: get().items.filter((i) => i.lineId !== lineId) }),

  updateQuantity: (lineId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(lineId);
      return;
    }
    set({
      items: get().items.map((i) =>
        i.lineId === lineId
          ? { ...i, quantity, total: quantity * num(i.unit_price) }
          : i
      ),
    });
  },

  setItemPrice: (lineId, price) =>
    set({
      items: get().items.map((i) =>
        i.lineId === lineId
          ? { ...i, unit_price: num(price), total: i.quantity * num(price) }
          : i
      ),
    }),

  clearCart: () => set({ items: [], customer: null, discount: 0, note: '', activeSeat: null, comensales: [] }),

  // Restore full cart state from a saved tab.
  // Las cuentas guardadas antes de los modificadores no traen lineId — se les
  // reconstruye para que sigan abriéndose sin romper nada.
  replaceCart: ({ items, customer, discount, note, comensales }) =>
    set({
      items: fundirDuplicados((items ?? []).map((i) => {
        // El precio se sana ANTES de copiar el resto: una cuenta guardada sin
        // `unit_price` seguía sin él al reabrirla, y a partir del primer cambio
        // de cantidad todo el carrito se iba a NaN.
        const unitPrice = unitPriceDe(i);
        const quantity  = num(i.quantity);
        return {
          ...i,
          quantity,
          unit_price: unitPrice,
          total:      quantity * unitPrice,
          modifiers:  i.modifiers ?? [],
          base_price: num(i.base_price ?? unitPrice),
          // fired_qty viaja dentro de la cuenta: al reabrir una mesa el POS
          // recuerda qué ya está en la cocina y no lo vuelve a mandar.
          fired_qty:  i.fired_qty ?? 0,
          // Las cuentas guardadas antes de los tiempos no lo traen: entran como
          // primer tiempo, que es lo que eran.
          course:     i.course ?? 1,
          // Las cuentas guardadas antes de los comensales entran como de la
          // mesa, que es lo que eran. `seat` suelto es del formato anterior,
          // cuando una línea solo podía ser de un comensal.
          seats:      normSeats(i.seats ?? i.seat ?? DE_LA_MESA),
          note:       normNota(i.note),
          lineId:     i.lineId ?? lineIdFor(i.product, i.modifiers ?? [], i.course ?? 1, normSeats(i.seats ?? i.seat), i.note),
          // Las cuentas de antes no traen `uid`: se les da el `lineId` que
          // tienen en este momento, que las dos tablets calculan igual.
          uid:        i.uid ?? i.lineId ?? lineIdFor(i.product, i.modifiers ?? [], i.course ?? 1, normSeats(i.seats ?? i.seat), i.note),
        };
      })),
      customer: customer ?? null,
      discount: discount ?? 0,
      note:     note ?? '',
      // Los comensales viajan con la cuenta. Una cuenta guardada antes de que
      // existieran no trae ninguno: se quedan los que haya deducido el carrito
      // de los propios platillos, que es justo lo que esa cuenta significaba.
      comensales: comensales === undefined
        ? normComensales((items ?? []).flatMap((i) => normSeats(i.seats ?? i.seat)).map((n) => ({ n })))
        : normComensales(comensales),
    }),

  // Cambia el tiempo de una línea. Si ya hay otra línea idéntica en el tiempo
  // destino se fusionan, para no dejar dos renglones del mismo platillo en el
  // mismo tiempo. Lo ya enviado a cocina viaja con la línea: se mandó como
  // entrada y así se queda, aunque el resto pase al fuerte.

  setActiveSeat: (seat) => set({ activeSeat: seat ?? null }),

  // ─── Comensales ──────────────────────────────────────────────────────────────

  setComensales: (lista) => set({ comensales: normComensales(lista) }),

  /**
   * Sienta a `cuantos` personas en la mesa.
   *
   * Es lo primero que sabe un mesero —"somos cuatro"— y hasta ahora era lo
   * último que el sistema le dejaba capturar. Nunca quita a alguien que ya
   * consumió: si la mesa se achica, los que tienen platillos se quedan, porque
   * su comida no se puede desaparecer con un stepper.
   */
  sentarComensales: (cuantos) => {
    const n = Math.max(0, Math.min(99, Math.trunc(Number(cuantos) || 0)));
    const previos  = get().comensales;
    const conAlgo  = new Set(get().items.flatMap((i) => normSeats(i.seats)));
    const nuevos   = [];
    for (let i = 1; i <= n; i += 1) nuevos.push(previos.find((c) => c.n === i) ?? { n: i, nombre: '' });
    for (const c of previos) if (c.n > n && conAlgo.has(c.n)) nuevos.push(c);
    for (const m of conAlgo) if (!nuevos.some((c) => c.n === m)) nuevos.push({ n: m, nombre: '' });
    set({ comensales: normComensales(nuevos) });
  },

  /**
   * Da de alta un comensal más y lo devuelve.
   *
   * Toma el número libre más chico, no "el último + 1": si el 2 se fue de la
   * mesa, el siguiente que llegue se sienta en el 2 y no en un 5 que no
   * corresponde a nadie.
   */
  addComensal: (nombre = '') => {
    const usados = new Set([
      ...get().comensales.map((c) => c.n),
      ...get().items.flatMap((i) => normSeats(i.seats)),
    ]);
    let n = 1;
    while (usados.has(n)) n += 1;
    if (n > 99) return null;
    set({ comensales: normComensales([...get().comensales, { n, nombre }]) });
    return n;
  },

  renameComensal: (n, nombre) =>
    set({
      comensales: get().comensales.map((c) =>
        (c.n === n ? { ...c, nombre: String(nombre ?? '').trim().slice(0, 12) } : c)),
    }),

  /**
   * Quita un comensal de la mesa.
   *
   * Lo suyo no se borra: pasa a ser de la mesa, y lo que compartía se queda con
   * los demás. Quitar a alguien no puede hacer desaparecer comida que ya se
   * pidió —y probablemente ya se sirvió—.
   */
  removeComensal: (n) => {
    const items = get().items.map((i) => {
      const suyos = normSeats(i.seats);
      if (!suyos.includes(n)) return i;
      const restantes = suyos.filter((x) => x !== n);
      return { ...i, seats: restantes, lineId: lineIdFor(i.product, i.modifiers ?? [], i.course ?? 1, restantes, i.note) };
    });
    set({
      items:      fundirDuplicados(items),
      comensales: get().comensales.filter((c) => c.n !== n),
      activeSeat: get().activeSeat === n ? null : get().activeSeat,
    });
  },

  /**
   * Cambia a quién pertenece una línea. Si el destino ya tiene el mismo
   * platillo con los mismos comensales se fusionan, igual que al cambiar de
   * tiempo: dos renglones idénticos serían un error de captura, no dos cosas.
   */
  setItemSeats: (lineId, seats) => {
    const items = get().items;
    const linea = items.find((i) => i.lineId === lineId);
    if (!linea) return;

    const lista = normSeats(seats);
    if (lista.join('-') === normSeats(linea.seats).join('-')) return;

    const nuevoId = lineIdFor(linea.product, linea.modifiers, linea.course ?? 1, lista, linea.note);
    const gemela  = items.find((i) => i.lineId === nuevoId && i.lineId !== lineId);

    if (gemela) {
      const qty = gemela.quantity + linea.quantity;
      set({
        items: items
          .filter((i) => i.lineId !== lineId)
          .map((i) => (i.lineId === nuevoId
            ? { ...i, quantity: qty, fired_qty: (i.fired_qty ?? 0) + (linea.fired_qty ?? 0), total: qty * num(i.unit_price) }
            : i)),
      });
      return;
    }

    set({
      items: items.map((i) => (i.lineId === lineId ? { ...i, lineId: nuevoId, seats: lista } : i)),
    });
  },

  /**
   * Los números de comensal que hay en la mesa, en orden.
   *
   * Es la UNIÓN de los que están sentados y los que tienen algo apuntado. Lo
   * segundo parece redundante y no lo es: una cuenta vieja, o una que venga de
   * otra tablet con una versión anterior, puede traer platillos de un comensal
   * que no está en la lista. Deducirlo de una sola de las dos fuentes perdería
   * platillos o perdería personas.
   */
  seatsMesa: () => [...new Set([
    ...get().comensales.map((c) => c.n),
    ...get().items.flatMap((i) => normSeats(i.seats)),
  ])].sort((a, b) => a - b),

  /**
   * Lo que le toca a cada comensal.
   *
   * Lo compartido —la botella, la entrada que picaron entre todos— se reparte
   * en partes iguales entre los comensales, que es lo que la gente entiende por
   * "compartido". El sobrante del redondeo cae en el último para que las partes
   * sumen exactamente el total de la mesa.
   *
   * Sin comensales capturados devuelve una sola parte con todo: una mesa que
   * nadie separó se cobra completa.
   */
  totalPorComensal: () => {
    const items = get().items;
    const seats = get().seatsMesa();
    const total = get().getTotal();

    // Nadie separó la mesa: se cobra completa, en una sola parte. Se mide por
    // platillos apuntados y no por comensales sentados, porque sentar a cuatro
    // no significa que quieran pagar por separado: mientras nadie asigne nada,
    // la mesa es una sola cuenta.
    if (!seats.length || !items.some((i) => normSeats(i.seats).length)) {
      return [{ seat: null, items, total }];
    }

    // Cada línea se reparte entre SUS comensales. Sin comensales es de la mesa
    // y se reparte entre todos. Así el plato de uno, la botella de dos y la
    // entrada de todos salen de la misma regla, sin casos especiales.
    const bruto = {};
    seats.forEach((n) => { bruto[n] = 0; });

    for (const i of items) {
      const suyos = normSeats(i.seats);
      const entre = suyos.length ? suyos : seats;
      const parte = num(i.total) / entre.length;
      entre.forEach((n) => { bruto[n] += parte; });
    }

    const brutoTotal = seats.reduce((a, n) => a + bruto[n], 0);

    // El descuento de la mesa se aplica en proporción a lo consumido: parejo le
    // regalaría más a quien menos pidió. Y el redondeo se cuadra al final —el
    // último absorbe la diferencia— para que las partes sumen EXACTAMENTE lo
    // que dice la cuenta. Un centavo de más y la caja no cierra.
    const factor = brutoTotal > 0 ? total / brutoTotal : 0;

    const comensales = get().comensales;
    const partes = seats.map((seat, n) => ({
      seat,
      // Cómo se llama esta parte en el cobro y en el ticket. "Parte 1" no le
      // sirve a nadie: el cajero no puede cotejarla contra la mesa.
      nombre: nombreDe(seat, comensales),
      items: items.filter((i) => {
        const suyos = normSeats(i.seats);
        return suyos.length ? suyos.includes(seat) : true;
      }),
      compartido: Math.round(
        items.reduce((a, i) => {
          const suyos = normSeats(i.seats);
          if (suyos.length === 1) return a;                       // solo suyo, no compartido
          const entre = suyos.length ? suyos : seats;
          return entre.includes(seat) ? a + num(i.total) / entre.length : a;
        }, 0) * 100) / 100,
      total: n === seats.length - 1 ? 0 : Math.round(bruto[seat] * factor * 100) / 100,
    }));

    const asignado = partes.slice(0, -1).reduce((a, p) => a + p.total, 0);
    partes[partes.length - 1].total = Math.round((total - asignado) * 100) / 100;

    return partes;
  },

  setCourse: (lineId, course) => {
    const items = get().items;
    const linea = items.find((i) => i.lineId === lineId);
    if (!linea || linea.course === course) return;

    const nuevoId = lineIdFor(linea.product, linea.modifiers, course, linea.seats ?? DE_LA_MESA, linea.note);
    const gemela  = items.find((i) => i.lineId === nuevoId && i.lineId !== lineId);

    if (gemela) {
      const qty = gemela.quantity + linea.quantity;
      set({
        items: items
          .filter((i) => i.lineId !== lineId)
          .map((i) => (i.lineId === nuevoId
            ? { ...i, quantity: qty, fired_qty: (i.fired_qty ?? 0) + (linea.fired_qty ?? 0), total: qty * num(i.unit_price) }
            : i)),
      });
      return;
    }

    set({
      items: items.map((i) =>
        i.lineId === lineId ? { ...i, lineId: nuevoId, course } : i
      ),
    });
  },

  /**
   * Nota de cocina de una línea ("sin cebolla", "término medio").
   *
   * Es por línea y no por venta porque la cocina emplata por platillo: la nota
   * general ya existía y servía para "alergia a nueces", no para decir cuál de
   * los tres tacos va sin cilantro. Cambia la identidad de la línea; si ya
   * había otra igual con la misma nota, se funden, como al cambiar de tiempo.
   */
  setItemNote: (lineId, note) => {
    const items = get().items;
    const linea = items.find((i) => i.lineId === lineId);
    if (!linea) return;

    const nota = normNota(note);
    if (nota === normNota(linea.note)) return;

    const nuevoId = lineIdFor(linea.product, linea.modifiers, linea.course ?? 1, linea.seats ?? DE_LA_MESA, nota);
    const gemela  = items.find((i) => i.lineId === nuevoId && i.lineId !== lineId);

    if (gemela) {
      const qty = gemela.quantity + linea.quantity;
      set({
        items: items
          .filter((i) => i.lineId !== lineId)
          .map((i) => (i.lineId === nuevoId
            ? { ...i, quantity: qty, fired_qty: (i.fired_qty ?? 0) + (linea.fired_qty ?? 0), total: qty * num(i.unit_price) }
            : i)),
      });
      return;
    }

    set({
      items: items.map((i) => (i.lineId === lineId ? { ...i, lineId: nuevoId, note: nota } : i)),
    });
  },

  // ─── Comandas ────────────────────────────────────────────────────────────────

  // Marca como enviado. Se llama SOLO después de que el servidor confirmó,
  // para que un fallo de red no deje platillos sin preparar.
  markFired: (lineIds) =>
    set({
      items: get().items.map((i) =>
        lineIds.includes(i.lineId) ? { ...i, fired_qty: i.quantity } : i
      ),
    }),

  // ─── Cliente ─────────────────────────────────────────────────────────────────

  setCustomer: (customer) => set({ customer }),
  clearCustomer: () => set({ customer: null }),

  // ─── Descuento ───────────────────────────────────────────────────────────────

  setDiscount: (amount) => set({ discount: Math.max(0, amount) }),
  setNote: (note) => set({ note }),

  // ─── Totales derivados ────────────────────────────────────────────────────────

  getSubtotal: () => get().items.reduce((acc, i) => acc + num(i.total), 0),

  getTotal: () => {
    const subtotal = get().getSubtotal();
    return Math.max(0, subtotal - get().discount);
  },

  getItemCount: () => get().items.reduce((acc, i) => acc + i.quantity, 0),

  // Cantidad total de un producto sumando todas sus líneas — el badge del
  // catálogo debe contar el platillo aunque esté pedido con variantes distintas.
  getProductQty: (productId) =>
    get().items
      .filter((i) => i.product._id === productId)
      .reduce((acc, i) => acc + i.quantity, 0),

  /**
   * La línea sobre la que actúan los controles del catálogo, que solo conocen
   * el producto. Antes usaban `product._id` como `lineId`, y eso solo acierta
   * con la línea pelona: en cuanto el platillo era de un comensal, de otro
   * tiempo o traía nota, el "−" del catálogo no hacía nada. Se prefiere la
   * línea que el catálogo habría creado ahora mismo (mismo comensal, sin
   * nota, primer tiempo) y, si no la hay, la última de ese producto.
   */
  _lineaDelCatalogo: (productId, seats = DE_LA_MESA) => {
    const items = get().items.filter((i) => i.product._id === productId);
    if (!items.length) return null;
    const lista = normSeats(seats).join('-');
    return items.find((i) => !normNota(i.note) && (i.course ?? 1) === 1 && normSeats(i.seats).join('-') === lista)
      ?? items[items.length - 1];
  },

  restarProducto: (productId, seats = DE_LA_MESA) => {
    const linea = get()._lineaDelCatalogo(productId, seats);
    if (linea) get().updateQuantity(linea.lineId, linea.quantity - 1);
  },

  // Deja el total del producto en `n` moviendo solo la línea del catálogo.
  fijarCantidadProducto: (productId, n, seats = DE_LA_MESA) => {
    const linea = get()._lineaDelCatalogo(productId, seats);
    if (!linea) return;
    const otras = get().getProductQty(productId) - linea.quantity;
    get().updateQuantity(linea.lineId, Math.max(0, n - otras));
  },
}));
